/**
 * <s-gallery-preview> — large product photo shown above the desktop gallery tiles.
 *
 * Gallery tiles target this element with commandfor + command="--show-media".
 * The preview copies the tile's image and repoints its own zoom button at the
 * tile's zoom dialog. When the preview is hidden (mobile), the tile opens its
 * zoom dialog directly instead.
 *
 * Required markup:
 *   [data-preview-zoom]   — button inside the preview that opens the zoom dialog.
 *   img[data-preview-image] — the large preview image.
 *   Tile buttons carry data-zoom-dialog (dialog id) and contain an <img>.
 */
class SGalleryPreview extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('command', this.#handleCommand, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #handleCommand = (event) => {
    if (event.command !== '--show-media') return;

    const tile = event.source;
    if (!(tile instanceof HTMLElement)) return;

    const dialogId = tile.dataset.zoomDialog;
    const visible = typeof this.checkVisibility === 'function' ? this.checkVisibility() : this.offsetParent !== null;

    if (!visible) {
      const dialog = dialogId ? document.getElementById(dialogId) : null;
      if (dialog instanceof HTMLDialogElement) dialog.showModal();
      return;
    }

    this.showTile(tile);
  };

  /**
   * Shows a gallery tile's photo in the large preview. Also called by
   * s-variant-picker when a buyer picks an option that has its own photo.
   * @param {HTMLElement} tile - Tile button carrying data-zoom-dialog.
   */
  showTile(tile) {
    if (!(tile instanceof HTMLElement)) return;
    const dialogId = tile.dataset.zoomDialog;

    const tileImage = tile.querySelector('img');
    const previewImage = this.querySelector('img[data-preview-image]');
    const zoomButton = this.querySelector('[data-preview-zoom]');

    if (tileImage && previewImage) {
      if (tileImage.srcset) previewImage.srcset = tileImage.srcset;
      previewImage.src = tileImage.currentSrc || tileImage.src;
      previewImage.alt = tileImage.alt;
    }

    if (zoomButton && dialogId) {
      zoomButton.setAttribute('commandfor', dialogId);
      zoomButton.setAttribute('aria-controls', dialogId);
      if (tile.getAttribute('aria-label')) zoomButton.setAttribute('aria-label', tile.getAttribute('aria-label'));
    }

    const gallery = tile.closest('[data-product-gallery]');
    for (const other of gallery?.querySelectorAll('[aria-current]') || []) other.removeAttribute('aria-current');
    tile.setAttribute('aria-current', 'true');
  }
}

if (!customElements.get('s-gallery-preview')) {
  customElements.define('s-gallery-preview', SGalleryPreview);
}
